import jQuery from '../utils/fl-jquery';
import '../utils/fl-jquery-ui-module';
import Handlebars from '../utils/handlebars';
import utils from '../utils/utils';
import constants from '../constants';

/*
    In this module we extend the jQuery-ui autocomplete widget.

    Init it by:
        jQuery(textFieldElementSelector).flAutocomplete(options);

    Additional Options:
        shopSystem - the shopSystem Object (e.g. PlentyMarkets)
        shopConfig - the shopConfig Object
*/
export default jQuery.widget('custom.flAutocomplete', jQuery.ui.autocomplete, {
    /**
     * Renders the entire result list
     * (This method overwrites the jQuery-ui widget method!)
     *
     * @param ul The ul as jQuery object
     * @param items The items to render (flat list)
     */
    _renderMenu(ul, items) {
        const self = this;
        const autocompleteType = this.options.shopConfig.autocompleteType;
        const isMobile = this.options.isMobile;
        let currentBlock = '';
        let indexWithinBlock = 1;
        let hasProducts = false;
        let hasCategoriesOrVendorsOrContentOrSuggest = false;

        // Refactoring old code that's on its way out just isn't worth it.
        // eslint-disable-next-line complexity
        jQuery.each(items, function (key, item) {
            // Prevent breaking when backend delivers an item with label = null
            if (item.label) {
                // Render block element if necessary, because items param is a flat ordered list.
                if (
                    item.hasOwnProperty('block') &&
                    item.block !== currentBlock &&
                    self.options.shopSystem.isResultV3(autocompleteType) &&
                    !isMobile
                ) {
                    const blockObj = self.renderBlockItem.call(self, item);
                    ul.append(blockObj);

                    currentBlock = item.block;

                    // Reset index to 1
                    indexWithinBlock = 1;
                }

                // For tracking purpose we save the index within the block
                item.indexWithinBlock = indexWithinBlock++;
                item.isEven = key % 2 === 0;

                const itemObj = self.renderResultItem(item);
                ul.append(itemObj);
            }

            if (item.block) {
                switch (item.block) {
                    case self.options.shopSystem.BLOCKS.CAT:
                    case self.options.shopSystem.BLOCKS.VENDOR:
                    case self.options.shopSystem.BLOCKS.CONTENT:
                    case self.options.shopSystem.BLOCKS.SUGGEST:
                    case self.options.shopSystem.BLOCKS.LANDING_PAGE:
                        hasCategoriesOrVendorsOrContentOrSuggest = true;
                        break;
                    case self.options.shopSystem.BLOCKS.PRODUCT:
                        hasProducts = true;
                        break;
                }
            }
        });

        // Add the allResult li element if not on mobile
        if (!this.options.isMobile) {
            ul.append(this.renderAllResultItem());
        }

        ul.addClass(this.options.shopSystem.flAcWrapperClassName);

        if (this.options.shopConfig.useTwoColumnLayout) {
            ul.addClass(this.options.shopSystem.flUseTwoColumnsLayoutClassName);
        }

        if (hasProducts && hasCategoriesOrVendorsOrContentOrSuggest && this.options.shopConfig.useTwoColumnLayout) {
            /*
             * Use the two-column layout only when there are actually two columns to be filled,
             * ie. (products && (cats || vendors))
             */
            ul.addClass(this.options.shopSystem.flTwoColumnLayoutWrapperClassName);
        } else {
            /*
             * Remove the two-column layout when there are not enough columns to fill,
             * this must be done explicitly as the wrapper is re-used on subsequent queries
             */
            ul.removeClass(this.options.shopSystem.flTwoColumnLayoutWrapperClassName);
        }

        this.afterRendering();
    },

    /**
     * Overridden _resizeMenu method to have a different behaviour on Mobile
     */
    _resizeMenu() {
        const ul = this.menu.element;
        if (this.options.isMobile) {
            // Autocomplete-Container should get the same width as the input-Field
            ul.outerWidth(this.element.outerWidth());
        } else {
            // Default-behaviour as defined in jquery-ui
            ul.outerWidth(Math.max(ul.width('').outerWidth() + 1, this.element.outerWidth()));
        }
    },

    /**
     * Executes after the result list has been rendered
     */
    afterRendering() {
        this.options.shopSystem.hidePopOver();
        this.options.shopSystem.bindEvents();

        return this;
    },

    /**
     * Renders result li-block
     *
     * @param item The block-item to render
     * @return jQuery Object
     */
    renderBlockItem(item) {
        item.blockLabel = this.options.shopConfig.blocks[item.block] || item.block;
        const blockObj = this._renderItem.call(this, item, this.options.shopSystem.resultBlockTemplateId);

        return blockObj;
    },

    /**
     * Renders result li-item
     *
     * @param item The item to render
     * @return jQuery Object
     */
    renderResultItem(item) {
        if (item.price && !isNaN(item.price)) {
            item.priceFormated = item.price.formatMoney();
        }

        if (item.basePrice && !isNaN(item.basePrice)) {
            item.basePriceFormated = item.basePrice.formatMoney();
        }

        const data = {};
        data.item = item;

        data.labelHighlighted = this.buildLabel(item);
        data.config = this.options.shopConfig;

        // If provided, highlight the ordernumber.
        if (data.item.ordernumber) {
            data.item.ordernumberHighlighted = this.highlightCurrentTerm(data.item.ordernumber, {
                highlightFromWordStart: false,
            });
        }

        const template = this.getItemTemplate(item);

        const itemObj = this._renderItem.call(this, data, template);

        // Add data-attr value (jquery-ui needs this)
        itemObj.data('ui-autocomplete-item', item);

        return itemObj;
    },

    /**
     * Returns correct html template id based on the item's type
     *
     * @param {Object} item The result item object
     * @return {String}
     */
    getItemTemplate(item) {
        let template = this.options.shopSystem.resultItemTemplateId;

        if (item.block === this.options.shopSystem.BLOCKS.PROMOTION && item.imageUrl) {
            template = this.options.shopSystem.resultPromotionItemId;
        } else if (item.block === this.options.shopSystem.BLOCKS.LANDING_PAGE) {
            template = this.options.shopSystem.resultLandingPageItemId;
        } else if (
            item.block === this.options.shopSystem.BLOCKS.CAT &&
            this.options.shopConfig.autocompleteType === constants.SMART_SUGGEST_TYPE_RESULT_V3
        ) {
            template = this.options.shopSystem.resultCatItemId;
        }

        return template;
    },

    /**
     * Renders all-result item
     *
     * @return jQuery Object
     */
    renderAllResultItem() {
        const data = {
            allResultMessage: this.options.shopConfig.blocks.allResult,
            term: this.term,
        };

        return this._renderItem.call(this, data, this.options.shopSystem.resultShowAllResultId);
    },

    /**
     * Gets template with item's data and returns jQuery Object with computed html
     * (This method overwrites the jQuery-ui widget method!)
     *
     * @param item The item to render
     * @param templateId The css-selector of the template
     *
     * @return jQuery Object
     */
    _renderItem(item, templateId) {
        let html;
        const customTemplate = jQuery(`script#${templateId}`);

        // Check if a custom handlebars template exists
        if (customTemplate.length === 1) {
            const source = customTemplate.html();
            const template = Handlebars.compile(source);
            html = template(item);
        } else {
            switch (templateId) {
                // Block-Header item element
                case this.options.shopSystem.resultBlockTemplateId:
                    html = this.getResultBlockTemplate(item);
                    break;
                // Result item element
                case this.options.shopSystem.resultItemTemplateId:
                    html = this.getResultItemTemplate(item);
                    break;
                // Promotion-Result item element
                case this.options.shopSystem.resultPromotionItemId:
                    html = this.getResultItemPromotionTemplate(item);
                    break;
                // All-result item element
                case this.options.shopSystem.resultShowAllResultId:
                    html = this.getResultShowAllResultsTemplate(item);
                    break;
                // Landing page item element
                case this.options.shopSystem.resultLandingPageItemId:
                    html = this.getResultItemLandingPageTemplate(item);
                    break;
                // Category item element
                case this.options.shopSystem.resultCatItemId:
                    html = this.getResultItemCatTemplate(item);
                    break;
            }
        }

        return jQuery(html);
    },

    /**
     * Constructs the final label - highlighted and with dividers if necessary
     *
     * @param {string} item in JSON-notation
     */
    buildLabel(item) {
        let label = this.highlightCurrentTerm(item.label);

        // Replace all underscores with an arrow when block is type 'cat'
        if (item.block === this.options.shopSystem.BLOCKS.CAT) {
            const internalCatDividerRegex = new RegExp('_', 'gi');
            label = label.replace(internalCatDividerRegex, this.options.shopSystem.catDivider);
        }

        return label;
    },

    /**
     * Wraps <span> tag around the current search term in the results label
     *
     * @param {string} label Label that was returned by FINDOLOGIC.
     * @param {{}} [options] Configurable options for highlighting.
     * @param {boolean} [options.highlightFromWordStart] If true (default) highlighting must start at the word.
     * Occurrences of the term within the word are ignored.
     */
    highlightCurrentTerm(label, options) {
        const defaultOptions = {
            highlightFromWordStart: true,
        };

        options = jQuery.extend({}, defaultOptions, options);
        const wordStartRegex = options.highlightFromWordStart ? '^' : '';

        // Replace special characters in the user input with pipes, so the non-special-character portion of the
        // term is still highlighted. This is important, because the search strips the input of special
        // characters, resulting in more suggestions.
        // Example: term == 'k!'; Processed by the search as 'k', resulting in suggestions 'Kite' and
        // 'Kite Repair Kit'. If we highlighted 'k!', nothing would be highlighted, although we found some
        // suggestions because of the 'k' in 'k!'.
        const term = jQuery.trim(this.term).replace(
            // Character range that covers pretty much any common writing system. This regex excludes special
            // characters such as "!", "," or ".".
            /[^\w\u007F-\uFFFF']+/gi,
            '|'
        );
        const highlightClass = this.options.shopSystem.flAcHighlightClassName;

        // Highlight only matches starting at the word boundary, because that's what the search suggests.
        // Everything that is not a German or Cyrillic word character is considered part of the word boundary.
        // Additionally, an underscore is considered word boundary and must be provided explicitly as a boundary
        // option, as it is part of the \w character class, which is inverted to define the boundary.
        const highlightRegex = new RegExp(
            `(?:${wordStartRegex}|_|` + // Allow nested category delimiter.
                `|\\s|` + // Allow extra spaces in between words.
                `[^\\w\u007F-\uFFFF']+)` + // Character range that covers pretty much any common writing system.
                `(${term})`,
            'ig'
        );

        function replacer(match, highlightFragment) {
            return match.replace(highlightFragment, `<span class="${highlightClass}">${highlightFragment}</span>`);
        }

        return label.replace(highlightRegex, replacer);
    },

    /**
     * Returns the html as string for the result-block-item
     *
     * @param {string} item in JSON-notation
     * @return {string} html-template
     */
    getResultBlockTemplate(item) {
        let html = `<li class="ui-autocomplete-category fl-block-${item.block}">`;
        html += `<h3>${item.blockLabel}</h3>`;
        html += '</li>';

        return html;
    },

    /**
     * Returns the html as string for the result-item
     *
     * @param {string} item in JSON-notation
     * @return {string} html-template
     */
    // Refactoring old code that's on its way out just isn't worth it.
    // eslint-disable-next-line complexity
    getResultItemTemplate(item) {
        let productPlacement = '';
        const itemEvenOddClass = item.item.isEven ? 'fl-item-even' : 'fl-item-odd';
        let html = '<li class="fl-item-{0} {1}" {2}>';

        if (item.item.productPlacement && this.options.shopConfig.autocompleteType === constants.SMART_SUGGEST_TYPE_RESULT_V3) {
            productPlacement = utils.formatString(
                'data-fl-product-placement="{0}"',
                utils.escapeHtmlSpecialChars(item.item.productPlacement)
            );
        }

        html = utils.formatString(html, item.item.block, itemEvenOddClass, productPlacement);

        /*
         * Jquery Ui expects the first child of the <li>-element to be an anchor element
         * Provide it, so that ui-state classes will be attached
         * (see http://api.jqueryui.com/autocomplete/#method-_renderItem)
         */
        html += '<a>';

        if (this.options.isMobile && item.item.block === 'suggest') {
            const suggestIconPath = `${this.options.shopConfig.cdnBaseUrl}/autocomplete/img/`;
            const suggestIconFile = 'suggest_arrow.svg';
            const suggestIconFallbackFile = 'suggest_arrow.png';

            html +=
                `<img class="fl-suggestion-icon" data-src="${suggestIconPath}${suggestIconFile}" ` +
                `data-fallback="${suggestIconPath}${suggestIconFallbackFile}">`;
        }

        if (item.item.imageUrl) {
            html += `<div class="col image"><img class="itemImage" src="${item.item.imageUrl}"></div>`;
        }

        if (this.options.shopConfig.autocompleteType === constants.SMART_SUGGEST_TYPE_RESULT_V3) {
            html += '<div class="fl-product-info-wrapper">';
        }
        html += `<div class="fl-title col label">${item.labelHighlighted}</div>`;

        if (item.item.ordernumber && this.options.shopConfig.autocompleteType === constants.SMART_SUGGEST_TYPE_RESULT_V3) {
            html += '<div class="col label fl-ordernumber">';
            html += `<span class="fl-label">${item.config.blocks.ordernumber}</span> `; // Intentional space.
            html += `<span class="fl-value">${item.item.ordernumberHighlighted}</span>`;
            html += '</div>';
        }

        if (this.options.shopConfig.autocompleteType === constants.SMART_SUGGEST_TYPE_RESULT_V3) {
            html += '</div>';
        }

        if (item.item.price) {
            html += '<div class="col price">';
            html += `<span class="price-label">${item.item.priceFormated} ${item.config.currency}</span>`;

            if (item.item.basePrice) {
                html += '<div class="basePrice">';
                html += `${item.item.basePriceFormated} ${item.config.currency} / ${item.item.basePriceUnit}`;
                html += '</div>';
            }

            html += '</div>';
        }

        if (item.showCart) {
            html += '<div class="col fl-shopping-cart-wrapper"></div>';
        }

        html += '</a>';
        html += '</li>';

        return html;
    },

    /**
     * Returns the html as string for the result-item-promotion
     *
     * @param {string} item in JSON-notation
     * @return {string} html-template
     */
    getResultItemPromotionTemplate(item) {
        const escapedName = utils.escapeHtmlSpecialChars(item.item.label);
        const escapedImageUrl = utils.escapeHtmlSpecialChars(item.item.imageUrl);

        const itemEvenOddClass = item.item.isEven ? 'fl-item-even' : 'fl-item-odd';
        let html = `<li class="fl-item-${item.item.block} fl-${item.item.block}-item ${itemEvenOddClass}" data-fl-promotion-name="${escapedName}">`;
        html += '<a>';
        html += `<div class="fl-image-wrapper"><img src="${escapedImageUrl}"></div>`;
        html += '</a>';
        html += '</li>';

        return html;
    },

    /**
     * Returns the html as string for the result-show-all-results-link
     *
     * @param {string} item in JSON-notation
     * @return {string} html-template
     */
    getResultShowAllResultsTemplate(item) {
        let html = '<li class="ui-autocomplete-category ui-autocomplete-allResults">';
        const termContainer = document.createElement('b');

        termContainer.textContent = item.term;
        html += `<h3>${item.allResultMessage} ${termContainer.outerHTML}</h3>`;
        html += '</li>';

        return html;
    },

    /**
     * Returns HTML as a string for displaying a landing page.
     *
     * @param {{}} item
     * @param {{}} item.item
     * @param {boolean} item.item.isEven An indicator whether the position in the sequence of items has an even or odd
     * index (for styling purposes).
     * @param {string} item.item.block The block in which the item resides (for styling and positioning purposes).
     * @return {string} The output HTML.
     */
    getResultItemLandingPageTemplate(item) {
        const label = utils.escapeHtmlSpecialChars(item.item.label);
        const labelHighlighted = item.labelHighlighted;
        const itemEvenOddClass = item.item.isEven ? 'fl-item-even' : 'fl-item-odd';

        return utils.formatString(
            '<li class="fl-item-{0} fl-{0}-item {1}" data-fl-landing-page-name="{2}">' +
                '<a>' +
                '<div class="col label">' +
                '{3}' +
                '</div>' +
                '</a>' +
                '</li>',
            item.item.block,
            itemEvenOddClass,
            label,
            labelHighlighted
        );
    },

    /**
     * Returns HTML as a string for displaying a category item.
     *
     * @param {{}} item
     * @param {{}} item.config Contains the shop config.
     * @param {{}} item.item SmartSuggest item that contains attributes like 'block' type and image url.
     * @param {boolean} item.item.isEven An indicator whether the position in the sequence of items has an even or
     * odd index (for styling purposes).
     * @param {string} item.item.block The block in which the item resides (for styling and positioning purposes).
     * @return {string} The output HTML.
     */
    getResultItemCatTemplate(item) {
        const itemEvenOddClass = item.item.isEven ? 'fl-item-even' : 'fl-item-odd';
        const divider = this.options.shopSystem.catDivider;
        const positionOfLastDivider = item.labelHighlighted.lastIndexOf(divider);
        let catMain = item.labelHighlighted;
        let catPrefixHtml = '';

        // If there is a prefix, split the string into prefix and main category.
        if (positionOfLastDivider >= 0) {
            const prefixLen = positionOfLastDivider + divider.length;
            catMain = item.labelHighlighted.substr(prefixLen);

            catPrefixHtml = utils.formatString('<p class="fl-cat-prefix">{0}</p>', item.labelHighlighted.substr(0, prefixLen));
        }

        const catMainHtml = utils.formatString('<p class="fl-cat-main">{0}</p>', catMain);

        return utils.formatString(
            '<li class="fl-item-{0} {1}">' + '<a>' + '<div class="col label">' + '{2}' + '{3}' + '</div>' + '</a>' + '</li>',
            item.item.block,
            itemEvenOddClass,
            catPrefixHtml,
            catMainHtml
        );
    },
});
